import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Card,
  Container,
  Stack,
  Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";

import type { LessonText, LessonTextSection } from "../api/course-service";
import CourseService from "../api/course-service";

import ENV from "../env";
import Markdown from "../components/markdown/Markdown";
import PageHeader from "../components/text/PageHeader";

/** The study material of one lesson: `# Text`, `# Example` and `# Pitfalls` as foldable sections. */
export default function LessonPage() {
    const [ lessonText, setLessonText ] = useState<LessonText | null>(null);
    // Several sections may stay open at once, so the reader can compare an example with its pitfalls
    const [ openSections, setOpenSections ] = useState<string[]>([]);

    const { id, lessonId } = useParams();
    const navigate = useNavigate();

    const lead = lessonText?.sections.filter(section => !section.title) ?? [];
    const folded = lessonText?.sections.filter(section => !!section.title) ?? [];

    function sectionSwitch(title: string) {
        setOpenSections(open => open.includes(title) ? open.filter(item => item !== title) : [ ...open, title ]);
    }

    useEffect(() => {
        if (!id || !lessonId) return;

        CourseService.lessonText(id, lessonId)
            .then(loaded => {
                setLessonText(loaded);
                // The first section is the lesson itself; the rest is opened when the reader asks for it
                setOpenSections(loaded.sections.filter(section => !!section.title).slice(0, 1)
                    .map(section => section.title ?? ''));
            })
            .catch(reason => {
                console.error("Unable to load text of lesson " + lessonId + ": ", reason);
                navigate("/404");
            });
    }, [ id, lessonId, navigate ]);

    // The effect above sends lessons without a text to /404; render nothing until the text is there
    if (!lessonText) return <></>;

    return <>
        <Helmet>
            <title>Lesson: { lessonText.title } | { ENV.APP_NAME }</title>
        </Helmet>

        <Container>
            <PageHeader title={ lessonText.title }>
                <Button component={ RouterLink } to={ `/courses/${ lessonText.courseId }` }
                        startIcon={ <ArrowBackIcon/> }>

                    { lessonText.courseTitle }
                </Button>
            </PageHeader>

            <Card sx={ { padding: '20px' } }>
                <Stack spacing={ 2 }>
                    <Typography variant="overline" sx={ { color: 'text.secondary' } }>
                        { lessonText.group }
                    </Typography>

                    { !!lessonText.short && <Typography>{ lessonText.short }</Typography> }

                    { lead.map((section, index) => (
                        <Markdown key={ `lead-${ index }` } text={ section.body }/>
                    )) }

                    <div>
                        { folded.map(section => (
                            <LessonSection key={ section.title } section={ section }
                                           opened={ openSections.includes(section.title ?? '') }
                                           sectionSwitch={ sectionSwitch }/>
                        )) }
                    </div>
                </Stack>
            </Card>
        </Container>
    </>;
}

type LessonSectionProps = {
    section: LessonTextSection;
    opened: boolean;
    sectionSwitch: (title: string) => void;
};

function LessonSection({ section, opened, sectionSwitch }: LessonSectionProps) {
    const title = section.title ?? '';
    // MUI labels the folded region by the id of its summary, so the section is named for screen readers
    const summaryId = `${ slug(title) }-summary`;
    const bodyId = `${ slug(title) }-body`;

    return <Accordion expanded={ opened } onChange={ () => sectionSwitch(title) } disableGutters>
        <AccordionSummary expandIcon={ <ExpandMoreIcon/> } id={ summaryId } aria-controls={ bodyId }>
            <Typography variant="h6">{ title }</Typography>
        </AccordionSummary>

        <AccordionDetails>
            <Markdown text={ section.body }/>
        </AccordionDetails>
    </Accordion>;
}

function slug(title: string): string {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}
